"use client";

import { useNoteLabel, useNoteNotation } from "@/hooks/use-notation";
import {
  relativeTonality,
  tonalityLabel,
  tonalityShortLabel,
  type Tonality,
} from "@/lib/music/tonality";
import { CheckIcon, LoaderIcon } from "../atoms/icons";
import {
  FONT_MONO,
  intervalLabel,
  signatureBadge,
  type ApplyState,
} from "./tonality-picker-helpers";
import { TonalityPickerTickSlider } from "./tonality-picker-tick-slider";

type Props = {
  source: Tonality;
  applied: Tonality | null;
  appliedSemis: number;
  staged: Tonality | null;
  stagedSemis: number;
  dirty: boolean;
  applyState: ApplyState;
  onStep: (delta: number) => void;
  onSetSemis: (semis: number) => void;
  onResetStaged: () => void;
  onApply: () => void;
};

export function TonalityPickerTransposePanel({
  source,
  applied,
  appliedSemis,
  staged,
  stagedSemis,
  dirty,
  applyState,
  onStep,
  onSetSemis,
  onResetStaged,
  onApply,
}: Props) {
  const noteNotation = useNoteNotation();

  const target = staged ?? applied ?? source;
  const targetRel = relativeTonality(target);
  const sourceRel = relativeTonality(source);
  const targetSig = signatureBadge(target);
  const stagedSign = stagedSemis > 0 ? "+" : "";
  const isLoading = applyState === "loading";
  const inputsDisabledClass = isLoading ? "opacity-50 pointer-events-none" : "";

  return (
    <div>
      {/* Header */}
      <div className="mb-3.5 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--color-text-muted)", fontFamily: FONT_MONO }}
          >
            Transposition
          </span>
          <span
            className="text-[10px] italic"
            style={{ color: "var(--color-text-secondary)" }}
          >
            · morceau en {tonalityShortLabel(source, noteNotation)}
          </span>
        </div>
        <button
          type="button"
          onClick={onResetStaged}
          disabled={!dirty || isLoading}
          className="text-[11px] disabled:cursor-default"
          style={{
            color:
              dirty && !isLoading
                ? "var(--color-text-secondary)"
                : "var(--color-text-muted)",
            cursor: dirty && !isLoading ? "pointer" : "default",
          }}
        >
          ↺ Annuler
        </button>
      </div>

      {/* Stepper row */}
      <div
        className={`mb-3.5 flex items-stretch gap-2 ${inputsDisabledClass}`}
      >
        <Stepper label="−" onClick={() => onStep(-1)} />
        <div
          className="relative flex flex-1 flex-col items-center justify-center rounded-[10px]"
          style={{
            background: "#0a0a0e",
            border: `1px solid ${
              dirty
                ? "var(--color-glass-border)"
                : "var(--color-border-subtle)"
            }`,
            height: 64,
          }}
        >
          {dirty && (
            <span
              className="absolute right-2 top-1.5 text-[8px] font-semibold uppercase tracking-[0.1em]"
              style={{
                color: "var(--color-accent-bright)",
                fontFamily: FONT_MONO,
              }}
            >
              EN ATTENTE
            </span>
          )}
          <div
            className="text-[24px] tabular-nums leading-none"
            style={{
              color: "var(--color-text-primary)",
              fontFamily: FONT_MONO,
              fontWeight: 300,
            }}
          >
            {stagedSign}
            {stagedSemis}
          </div>
          <div
            className="mt-[3px] text-[11px] italic"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {intervalLabel(stagedSemis)}
          </div>
        </div>
        <Stepper label="+" onClick={() => onStep(1)} />
      </div>

      {/* Origin → target preview */}
      <div
        className={`mb-3 flex items-baseline justify-center gap-3 text-center ${
          isLoading ? "opacity-50" : ""
        }`}
      >
        <div className="whitespace-nowrap">
          <div
            className="text-[16px] tabular-nums"
            style={{
              color: "var(--color-text-secondary)",
              fontFamily: FONT_MONO,
            }}
          >
            {tonalityLabel(source, noteNotation)}
          </div>
          <div
            className="mt-0.5 text-[10px] italic"
            style={{ color: "var(--color-text-muted)" }}
          >
            rel. {tonalityLabel(sourceRel, noteNotation)}
          </div>
        </div>
        <div
          className="text-[14px]"
          style={{ color: "var(--color-text-muted)" }}
        >
          →
        </div>
        <div className="whitespace-nowrap">
          <div className="flex items-baseline justify-center gap-1.5">
            <span
              className="text-[16px] tabular-nums"
              style={{
                color: "var(--color-accent-bright)",
                fontFamily: FONT_MONO,
                fontWeight: 500,
              }}
            >
              {tonalityLabel(target, noteNotation)}
            </span>
            {targetSig && (
              <span
                className="text-[10px] tabular-nums"
                style={{ color: "var(--color-text-muted)" }}
              >
                {targetSig}
              </span>
            )}
          </div>
          <div
            className="mt-0.5 text-[10px] italic"
            style={{ color: "var(--color-text-muted)" }}
          >
            rel. {tonalityLabel(targetRel, noteNotation)}
          </div>
        </div>
      </div>

      <TonalityPickerTickSlider
        value={stagedSemis}
        onChange={onSetSemis}
        appliedSemis={appliedSemis}
        disabled={isLoading}
      />
      <div
        className="flex justify-between text-[10px] tabular-nums"
        style={{ color: "var(--color-text-muted)", fontFamily: FONT_MONO }}
      >
        <span>−2 oct</span>
        <span>0</span>
        <span>+2 oct</span>
      </div>

      {/* Apply bar, switches between idle / loading / done */}
      <div
        className="mt-3.5 border-t pt-3.5"
        style={{ borderColor: "var(--color-border-subtle)" }}
      >
        <ApplyBar
          applyState={applyState}
          dirty={dirty}
          stagedSemis={stagedSemis}
          source={source}
          applied={applied}
          onApply={onApply}
        />
      </div>
    </div>
  );
}

type ApplyBarProps = {
  applyState: ApplyState;
  dirty: boolean;
  stagedSemis: number;
  source: Tonality;
  applied: Tonality | null;
  onApply: () => void;
};

function ApplyBar({
  applyState,
  dirty,
  stagedSemis,
  source,
  applied,
  onApply,
}: ApplyBarProps) {
  if (applyState === "loading") {
    return <LoadingBar stagedSemis={stagedSemis} />;
  }

  if (applyState === "done") {
    return <DoneBar source={source} applied={applied ?? source} />;
  }

  return <IdleBar dirty={dirty} stagedSemis={stagedSemis} onApply={onApply} />;
}

type IdleBarProps = {
  dirty: boolean;
  stagedSemis: number;
  onApply: () => void;
};

function IdleBar({ dirty, stagedSemis, onApply }: IdleBarProps) {
  const sign = stagedSemis > 0 ? "+" : "";

  return (
    <div className="flex items-center gap-2">
      <span
        className="flex-1 text-[10px] italic"
        style={{ color: "var(--color-text-muted)" }}
      >
        {dirty
          ? `Cliquer "Appliquer" pour transposer le morceau · ${sign}${stagedSemis} st`
          : "Tonalité d'origine verrouillée par le morceau"}
      </span>
      <button
        type="button"
        onClick={onApply}
        disabled={!dirty}
        className="rounded-lg px-4 py-[7px] text-[12px] font-semibold transition-colors disabled:cursor-default"
        style={{
          background: dirty ? "var(--color-accent)" : "#1a1a22",
          color: dirty ? "#fff" : "var(--color-text-muted)",
          border: `1px solid ${
            dirty ? "var(--color-accent)" : "var(--color-border)"
          }`,
          boxShadow: dirty ? "0 0 16px rgba(139,92,246,0.35)" : "none",
          cursor: dirty ? "pointer" : "default",
        }}
      >
        Appliquer{dirty ? ` ${sign}${stagedSemis}` : ""}
      </button>
    </div>
  );
}

type LoadingBarProps = {
  stagedSemis: number;
};

function LoadingBar({ stagedSemis }: LoadingBarProps) {
  const sign = stagedSemis > 0 ? "+" : "";

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LoaderIcon
            size={14}
            className="animate-spin"
            style={{ color: "var(--color-accent)" }}
          />
          <span
            className="text-[12px]"
            style={{ color: "var(--color-text-primary)", fontWeight: 500 }}
          >
            Transposition en cours…
          </span>
        </div>
        <span
          className="text-[11px] tabular-nums"
          style={{
            color: "var(--color-text-secondary)",
            fontFamily: FONT_MONO,
          }}
        >
          {sign}
          {stagedSemis} st
        </span>
      </div>
      <div
        className="overflow-hidden rounded-full"
        style={{ height: 3, background: "#1a1a22" }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: "40%",
            background: "var(--color-accent)",
            animation: "indeterminate 1.2s ease-in-out infinite",
          }}
        />
      </div>
      <div
        className="mt-1.5 text-[10px] italic"
        style={{ color: "var(--color-text-muted)" }}
      >
        Recalcul des notes du morceau · ne ferme pas la fenêtre
      </div>
    </div>
  );
}

type DoneBarProps = {
  source: Tonality;
  applied: Tonality;
};

function DoneBar({ source, applied }: DoneBarProps) {
  const noteNotation = useNoteNotation();
  const toNoteLabel = useNoteLabel();

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex h-4 w-4 items-center justify-center rounded-full"
          style={{
            background: "rgba(52, 211, 153, 0.18)",
            color: "var(--color-ok)",
          }}
        >
          <CheckIcon size={10} strokeWidth={3} />
        </span>
        <span
          className="text-[12px]"
          style={{ color: "var(--color-text-primary)" }}
        >
          Transposition appliquée
        </span>
      </div>
      <span
        className="text-[11px] tabular-nums"
        style={{
          color: "var(--color-text-secondary)",
          fontFamily: FONT_MONO,
        }}
      >
        {toNoteLabel(source.tonic)} →{" "}
        {tonalityShortLabel(applied, noteNotation)}
      </span>
    </div>
  );
}

type StepperProps = {
  label: string;
  onClick: () => void;
};

function Stepper({ label, onClick }: StepperProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[10px] cursor-pointer transition-colors hover:bg-[var(--color-bg-hover)]"
      style={{
        width: 56,
        height: 64,
        background: "#0a0a0e",
        border: "1px solid var(--color-border-subtle)",
        color: "var(--color-text-secondary)",
        fontSize: 22,
      }}
    >
      {label}
    </button>
  );
}
